import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import {
	catchError,
	distinctUntilChanged,
	EMPTY,
	finalize,
	map,
	Subject,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs';

import {
	AntonniaContractAttachmentSummary,
	AntonniaContractSummary,
} from '../../../../modules/organizations/dtos/antonnia-contract-summary';
import { OrganizationsService } from '../../../../modules/organizations/organizations.service';
import { SnackBarService } from '../../../../shared/components/snack-bar/snack-bar.service';
import { RouteUtils } from '../../../../shared/utils/route/route.util';

type ContractsViewState = 'empty' | 'error' | 'loading' | 'success';
type ContractsViewMode = 'advanced' | 'guided';

const CONFIGURATION_ERROR_CODES = new Set([
	'ANTONNIA_AUTHENTICATION_EXPIRED',
	'ANTONNIA_AUTHENTICATION_REQUIRED',
]);
const MAX_ATTACHMENT_SIZE_BYTES = 15 * 1024 * 1024;

@Component({
	selector: 'app-contracts',
	styleUrls: ['./contracts.component.scss'],
	templateUrl: './contracts.component.html',
})
export class ContractsComponent implements OnInit, OnDestroy {
	contractContentControl = new FormControl('', { nonNullable: true });
	contracts: AntonniaContractSummary[] = [];
	editingContractId: string | null = null;
	errorMessage: string = '';
	isAuthenticating: boolean = false;
	isAuthenticationRequired: boolean = false;
	isCreating: boolean = false;
	isDownloadingAttachmentId: string | null = null;
	isImportingContractId: string | null = null;
	isImportingNew: boolean = false;
	isSaving: boolean = false;
	pendingAttachment: File | null = null;
	state: ContractsViewState = 'loading';
	viewMode: ContractsViewMode = 'guided';

	private organizationId: string = '';
	private readonly unsubscribe$ = new Subject<void>();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly organizationsService: OrganizationsService,
		private readonly snackBarService: SnackBarService,
	) {}

	get isEmpty(): boolean {
		return this.state === 'empty';
	}

	get isError(): boolean {
		return this.state === 'error';
	}

	get isLoading(): boolean {
		return this.state === 'loading';
	}

	get isSuccess(): boolean {
		return this.state === 'success';
	}

	authenticate(): void {
		this.isAuthenticating = true;
		this.organizationsService
			.getAntonniaOAuthAuthorization(this.organizationId)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: () => {
					this.isAuthenticating = false;
					this.snackBarService.showSnack(
						'Não foi possível iniciar a conexão com a Antonnia.',
						'error',
					);
				},
				next: ({ url }) => window.location.assign(url),
			});
	}

	cancelCreating(): void {
		this.isCreating = false;
		this.contractContentControl.setValue('');
		this.pendingAttachment = null;
	}

	cancelEditing(): void {
		this.editingContractId = null;
		this.contractContentControl.setValue('');
		this.pendingAttachment = null;
	}

	downloadAttachment(
		contract: AntonniaContractSummary,
		attachment: AntonniaContractAttachmentSummary,
	): void {
		if (this.isDownloadingAttachmentId) {
			return;
		}

		this.isDownloadingAttachmentId = attachment.id;
		this.organizationsService
			.downloadAntonniaContractAttachment(
				this.organizationId,
				contract.id,
				attachment.id,
			)
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isDownloadingAttachmentId = null)),
			)
			.subscribe({
				error: () =>
					this.snackBarService.showSnack(
						'Não foi possível baixar o anexo.',
						'error',
					),
				next: (blob) => this.saveBlobLocally(blob, attachment.name),
			});
	}

	formatFileSize(sizeBytes: number): string {
		if (sizeBytes < 1024) {
			return `${sizeBytes} B`;
		}

		if (sizeBytes < 1024 * 1024) {
			return `${(sizeBytes / 1024).toFixed(1)} KB`;
		}

		return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
	}

	importAttachment(contract: AntonniaContractSummary, event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];

		input.value = '';

		if (!file || this.isImportingContractId) {
			return;
		}

		if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
			this.snackBarService.showSnack(
				'O anexo deve ter no máximo 15 MB.',
				'error',
			);

			return;
		}

		this.isImportingContractId = contract.id;
		this.organizationsService
			.generateAntonniaContractDraft(this.organizationId, file)
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isImportingContractId = null)),
			)
			.subscribe({
				error: (error: HttpErrorResponse) => {
					const message =
						typeof error.error?.message === 'string'
							? error.error.message
							: 'Não foi possível analisar o anexo.';

					this.snackBarService.showSnack(message, 'error');
				},
				next: ({ content }) => {
					this.editingContractId = contract.id;
					this.contractContentControl.setValue(content);
					this.pendingAttachment = file;
					this.snackBarService.showSnack(
						'Anexo analisado. Revise o conteúdo antes de salvar.',
						'success',
					);
				},
			});
	}

	importNewAttachment(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];

		input.value = '';

		if (!file || this.isImportingNew) {
			return;
		}

		if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
			this.snackBarService.showSnack(
				'O anexo deve ter no máximo 15 MB.',
				'error',
			);

			return;
		}

		this.isImportingNew = true;
		this.organizationsService
			.generateAntonniaContractDraft(this.organizationId, file)
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isImportingNew = false)),
			)
			.subscribe({
				error: (error: HttpErrorResponse) => {
					const message =
						typeof error.error?.message === 'string'
							? error.error.message
							: 'Não foi possível analisar o anexo.';

					this.snackBarService.showSnack(message, 'error');
				},
				next: ({ content }) => {
					this.startCreating(content, file);
					this.snackBarService.showSnack(
						'Anexo analisado. Revise o novo contrato antes de criar.',
						'success',
					);
				},
			});
	}

	isEditing(contract: AntonniaContractSummary): boolean {
		return this.editingContractId === contract.id;
	}

	isImporting(contract: AntonniaContractSummary): boolean {
		return this.isImportingContractId === contract.id;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.activatedRoute.paramMap
			.pipe(
				map((params) =>
					RouteUtils.getRequiredParam({
						param: RouteUtils.params.organizationId,
						params,
					}),
				),
				distinctUntilChanged(),
				tap((organizationId) => this.prepareOrganizationChange(organizationId)),
				switchMap((organizationId) =>
					this.organizationsService.getAntonniaContracts(organizationId).pipe(
						catchError((error: HttpErrorResponse) => {
							this.handleError(error);

							return EMPTY;
						}),
					),
				),
				takeUntil(this.unsubscribe$),
			)
			.subscribe((contracts) => {
				this.contracts = contracts;
				this.state = contracts.length > 0 ? 'success' : 'empty';
			});
	}

	saveContract(contract: AntonniaContractSummary): void {
		if (this.isSaving) {
			return;
		}

		const content = this.contractContentControl.value;
		this.isSaving = true;
		const organizationId = this.organizationId;

		this.organizationsService
			.updateAntonniaContract(
				organizationId,
				contract.id,
				{ content },
				this.pendingAttachment,
			)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error: HttpErrorResponse) => {
					if (organizationId === this.organizationId) {
						this.handleSaveError(error);
					}
				},
				next: (updatedContract) => {
					if (organizationId !== this.organizationId) {
						return;
					}

					this.contracts = this.contracts.map((currentContract) =>
						currentContract.id === updatedContract.id
							? updatedContract
							: currentContract,
					);
					this.editingContractId = null;
					this.contractContentControl.setValue('');
					this.pendingAttachment = null;
					this.isSaving = false;
					const updatedByName = updatedContract.updatedBy?.name;
					this.snackBarService.showSnack(
						updatedByName
							? `Contrato salvo na Antonnia por ${updatedByName}.`
							: 'Contrato salvo na Antonnia.',
						'success',
					);
				},
			});
	}

	saveNewContract(): void {
		if (this.isSaving) {
			return;
		}

		const content = this.contractContentControl.value.trim();

		if (!content) {
			this.snackBarService.showSnack(
				'Adicione ao menos uma informação ao contrato.',
				'error',
			);

			return;
		}

		this.isSaving = true;
		const organizationId = this.organizationId;
		this.organizationsService
			.createAntonniaContract(organizationId, content, this.pendingAttachment)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error: HttpErrorResponse) => {
					if (organizationId === this.organizationId) {
						this.handleSaveError(error);
					}
				},
				next: (contract) => {
					if (organizationId !== this.organizationId) {
						return;
					}

					this.contracts = [contract];
					this.state = 'success';
					this.isSaving = false;
					this.isCreating = false;
					this.pendingAttachment = null;
					this.contractContentControl.setValue('');
					this.snackBarService.showSnack(
						contract.updatedBy?.name
							? `Contrato criado na Antonnia por ${contract.updatedBy.name}.`
							: 'Contrato criado na Antonnia.',
						'success',
					);
				},
			});
	}

	setViewMode(mode: ContractsViewMode): void {
		this.viewMode = mode;
	}

	startCreating(
		content = '## Nova seção\n\n',
		attachment: File | null = null,
	): void {
		this.isCreating = true;
		this.contractContentControl.setValue(content);
		this.pendingAttachment = attachment;
	}

	startEditing(contract: AntonniaContractSummary): void {
		this.editingContractId = contract.id;
		this.contractContentControl.setValue(contract.content);
		this.pendingAttachment = null;
	}

	private handleError(error: HttpErrorResponse): void {
		const code = typeof error.error?.code === 'string' ? error.error.code : '';

		if (CONFIGURATION_ERROR_CODES.has(code)) {
			this.isAuthenticationRequired = true;
			this.errorMessage =
				'Conecte-se à Antonnia para visualizar os contratos desta organização.';
		} else if (
			code === 'ANTONNIA_MCP_UNAVAILABLE' ||
			error.status === 0 ||
			error.status === 503
		) {
			this.errorMessage =
				'Não foi possível consultar os contratos agora. Tente novamente mais tarde.';
		} else {
			this.errorMessage = 'Não foi possível carregar os contratos.';
		}

		this.state = 'error';
	}

	private handleSaveError(error: HttpErrorResponse): void {
		const code = typeof error.error?.code === 'string' ? error.error.code : '';
		let message: string;

		this.isSaving = false;

		if (code === 'ANTONNIA_BRANCH_NOT_INITIALIZED') {
			message =
				'A branch de edição da Antonnia ainda não contém o agente atual. Solicite à Antonnia a inicialização da branch main.';
		} else if (
			error.status === 409 &&
			typeof error.error?.message === 'string'
		) {
			message = error.error.message;
		} else if (
			code === 'ANTONNIA_OPERATION_REJECTED' &&
			typeof error.error?.message === 'string'
		) {
			message = error.error.message;
		} else if (
			code === 'ANTONNIA_MCP_UNAVAILABLE' ||
			error.status === 0 ||
			error.status === 503
		) {
			message =
				'A Antonnia está temporariamente indisponível. Tente salvar novamente.';
		} else {
			message = 'Não foi possível salvar o contrato na Antonnia.';
		}

		this.snackBarService.showSnack(message, 'error');
	}

	private prepareOrganizationChange(organizationId: string): void {
		this.organizationId = organizationId;
		this.contracts = [];
		this.errorMessage = '';
		this.isAuthenticating = false;
		this.isAuthenticationRequired = false;
		this.isDownloadingAttachmentId = null;
		this.isCreating = false;
		this.isImportingNew = false;
		this.isImportingContractId = null;
		this.isSaving = false;
		this.state = 'loading';
		this.cancelEditing();
	}

	private saveBlobLocally(blob: Blob, fileName: string): void {
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');

		anchor.href = url;
		anchor.download = fileName;
		anchor.click();
		URL.revokeObjectURL(url);
	}
}
